import React, { useContext, useEffect, useState } from "react";
import { RentalDatesType } from "@glamcorner/components/lib/core/components/RentalDatePickerField";
import { StyleDesignations } from "../../../common/interfaces";
import { useURLParameter } from "./URLParametersProvider";
import { ServerGateway } from "../../services/ServerGateway";
import { Bus } from "../../services/Bus";
import { DATE_PICKER_EVENTS, POSTCODE_SERVICE_EVENTS } from "../../constants";
import { useDesignations } from "./DesignationProvider";

const emptySendDates = { startDate: "", endDate: "" };

export interface RentalContextProviderValue {
  availableDeliveryDates: string[];
  errors: { size?: string; postcode?: string; rentalDates?: string };
  postcode: string | undefined;
  rentalDates: RentalDatesType | typeof emptySendDates;
  rentalPeriod: number;
  setPostcode: (newPostcode: string) => void;
  setRentalDates: (newDates: RentalDatesType) => void;
  setRentalPeriod: (newPeriod: number) => void;
  validateRental: () => boolean;
}

export const rentalContextDefaults: RentalContextProviderValue = {
  availableDeliveryDates: [],
  errors: {},
  postcode: undefined,
  rentalDates: { startDate: "", endDate: "" },
  rentalPeriod: 4,
  setPostcode: () => {},
  setRentalDates: () => {},
  setRentalPeriod: () => {},
  validateRental: () => false,
};

export const RentalContext = React.createContext<RentalContextProviderValue>(rentalContextDefaults);

// This is a context provider only because the magneto bits need to know if we're changing rental periods
export const RentalProvider: React.FC = ({ children }) => {
  const { postCode: postCodeFromURL, rentalDates: rentalDatesFromURL } = useURLParameter();
  const { designation, errors: designationErrors, productCode, isValid } = useDesignations();

  // A gate to only execute useEffects when component has been mounted and is selected
  const [isActive, setIsActive] = useState(false);

  const initialSendDates = rentalDatesFromURL || emptySendDates;
  const [rentalDates, setRentalDates] = useState<RentalDatesType | typeof emptySendDates>(initialSendDates);
  const [postcode, setPostcode] = useState<string | undefined>(postCodeFromURL || "");
  const [availableDeliveryDates, setAvailableDeliveryDates] = useState<string[]>([]);
  const [rentalPeriod, setRentalPeriod] = useState<number>(4);
  const [errors, setErrors] = useState<{
    size?: string;
    postcode?: string;
    rentalDates?: string;
  }>(designationErrors);

  const serverGateway = new ServerGateway();

  useEffect(() => {
    setIsActive(designation === StyleDesignations.RENTAL);
  }, [designation]);

  useEffect(() => {
    setErrors((errors) => ({ ...errors, ...designationErrors }));
  }, [designationErrors]);

  useEffect(() => {
    if (isActive) {
      setRentalDates(emptySendDates);
    }
  }, [productCode]);

  useEffect(() => {
    if (isActive) {
      setErrors((errors) => ({ ...errors, postcode: undefined }));
      Bus.broadcast(POSTCODE_SERVICE_EVENTS.SET_POSTCODE, { postcode });
      setRentalDates(emptySendDates);
    }
  }, [postcode]);

  useEffect(() => {
    if (isActive) {
      setRentalDates(emptySendDates);
    }
  }, [rentalPeriod]);

  useEffect(() => {
    if (isActive) {
      setErrors((errors) => ({ ...errors, rentalDates: undefined }));
      Bus.broadcast(DATE_PICKER_EVENTS.DATE_SELECTED, { dates: rentalDates });
      Bus.broadcast(DATE_PICKER_EVENTS.DATE_CHANGED, { dates: rentalDates });
    }
  }, [rentalDates]);

  useEffect(() => {
    if (!postcode || !productCode || !rentalPeriod) {
      return;
    }

    serverGateway
      .productCalendar({
        postcode,
        productCode,
        rentalDays: rentalPeriod.toString(),
      })
      .then((response) => {
        if (response && response.available_delivery_dates) {
          setAvailableDeliveryDates(response.available_delivery_dates);
        }
      });
  }, [productCode, postcode, rentalPeriod]);

  const validateRental = () => {
    let isRentalValid = isValid();

    if (!productCode) {
      isRentalValid = false;
      setErrors((errors) => ({ ...errors, size: "Required" }));
    }

    if (!postcode) {
      isRentalValid = false;
      setErrors((errors) => ({ ...errors, postcode: "Required" }));
    }

    if (rentalDates === emptySendDates || !rentalDates.startDate || !rentalDates.endDate) {
      isRentalValid = false;
      setErrors((errors) => ({ ...errors, rentalDates: "Required" }));
    } else if (!availableDeliveryDates.includes(rentalDates.startDate)) {
      // No check for end date, can fall on weekend
      isRentalValid = false;
      setErrors((errors) => ({ ...errors, rentalDates: "Invalid" }));
    }

    return isRentalValid;
  };

  const contextValue: RentalContextProviderValue = {
    availableDeliveryDates,
    errors,
    postcode,
    rentalDates,
    rentalPeriod,
    setPostcode,
    setRentalDates,
    setRentalPeriod,
    validateRental,
  };

  return <RentalContext.Provider value={contextValue}>{children}</RentalContext.Provider>;
};

export const useRental = () => useContext(RentalContext);
